import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import CreateJobForm from '../CreateJobForm/CreateJobForm';
import { useDispatch, useSelector } from 'react-redux';

import { jobRequest, getJobRequest, updateJobRequest } from '../../actions';
import Loader from '../Loader/Loader';
import axios from '../../services';
import mapQuotationToCreateJobForm from '../../utils/mapQuotationToCreateJobForm';
import toaster from '../../utils/toaster';

const useStyles = makeStyles((theme) => ({
  page: {
    maxWidth: 1040,
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(6),
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 600,
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
    color: theme.palette.text.primary,
  },
  subtitle: {
    marginTop: theme.spacing(0.75),
    color: theme.palette.text.secondary,
    fontWeight: 400,
    letterSpacing: '0.01em',
    lineHeight: 1.5,
  },
  hint: {
    marginTop: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    letterSpacing: '0.01em',
  },
}));

const CreateJobPage = () => {
  
  const classes = useStyles();
  const dispatch = useDispatch();
  const { jobId } = useParams();
  const history = useHistory();
  const location = useLocation();

  const quotationId = useMemo(
    () => new URLSearchParams(location.search).get('quotationId'),
    [location.search]
  );

  const [quotationPrefill, setQuotationPrefill] = useState(null);

  const { job } = useSelector((state) => state.Job);
  const { loader } = useSelector((state) => state.Loader);

  const isEditing = !!jobId;

  useEffect(() => {
    if (!quotationId || isEditing) {
      setQuotationPrefill(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`quotations/${quotationId}`)
      .then((res) => {
        if (!cancelled) setQuotationPrefill(mapQuotationToCreateJobForm(res.data));
      })
      .catch((err) => {
        if (!cancelled) {
          toaster(err?.response?.data?.message || 'Could not load quotation for prefill', { type: 'error' });
          setQuotationPrefill(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [quotationId, isEditing]);

  const onCreateJob = (data) => {
    const payload = { ...data };
    if (quotationId) payload.sourceQuotationId = quotationId;
    dispatch(jobRequest(payload));
    history.push(`/download-job`);
  };

  const onUpdateJob = (data) => {
    dispatch(updateJobRequest(data, jobId));
  };

  const jobToUpdate = useMemo(() => {
    return job;
  }, [loader, job]);

  return (
    <Box className={classes.page}>
      <header className={classes.header}>
        <Typography variant='h4' component='h1' className={classes.title}>
          {isEditing ? 'Update job' : 'Create job'}
        </Typography>
        <Typography variant='body1' className={classes.subtitle}>
          Enter consignment and contact details. Required fields are marked on the form.
        </Typography>
        {quotationId && !isEditing && (
          <Typography variant='body2' className={classes.hint}>
            Prefilled from quotation{quotationPrefill ? '' : ' (loading…)'} — review all fields before submitting.
          </Typography>
        )}
      </header>
      {loader ? (
        <Loader />
      ) : (
        <CreateJobForm
          onCreateJob={onCreateJob}
          onUpdateJob={onUpdateJob}
          isEditing={isEditing}
          job={jobToUpdate}
          quotationPrefill={quotationPrefill}
        />
      )}
    </Box>
  );
};

export default CreateJobPage;
